// libs
import cx from "classnames";
import React, { ReactElement, useCallback, useState, useRef, RefObject, useEffect, useMemo } from "react";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { TextButton } from "@swiggy-private/dls-web-core/dist/components/TextButton";

// components
import { BottomSheet, BottomSheetChildrenProps, isEmpty, useEffectOnce, BackIcon } from "@swiggy-private/core-ui";

// utils
import { useFocusTrap } from "@swiggy-private/core-ui";
import { getIconCloudinaryUrl as getCloudinaryUrl } from "@payment-ui/helpers/Icon";

//constants
import { ACCESSIBILITY_ROLES, TEXTS } from "@payment-ui/constants/index";
import { PAYMENT_ALERT_HEADER_TITLE_ID, PAYMENT_ALERT_CONTENT_ID } from "@payment-ui/constants/PaymentAlert";

// test-ids
import { PAYMENT_ALERT_TEST_IDS } from "@swiggy-private/payment-test-ids";

//hooks
import { useFocusDialog } from "@swiggy-private/core-ui";
import { getActiveElement } from "@swiggy-private/core-ui";

// styles
import styles from "./SecureCardInfo.scss";

// types
import { JuspayTokenizationMeta } from "@swiggy-private/payment-types";

export interface CheckboxCardActionProps {
    text: string;
    onClick: () => void;
    showArrow?: boolean;
    subText?: string;
}

export interface CheckboxCardInfoProps {
    data: JuspayTokenizationMeta;
    primaryAction?: CheckboxCardActionProps;
    secondaryAction?: CheckboxCardActionProps;
    hideOnOverlayClick?: boolean;
    onClose?: VoidFunction;
    cardBrand: string;
    onLoad?: VoidFunction;
}

type SecureCardInfoContentProps = Omit<CheckboxCardInfoProps, "hideOnOverlayClick" | "onClose"> & {
    setAction: (fn: VoidFunction) => void;
};

const getCardBrandFormattedString = (text: string, cardBrand: string): string => {
    return text && text.replace("{{cardBrand}}", isEmpty(cardBrand) ? TEXTS.CARD_NETWORK : cardBrand);
};

const ARROW_COLOR = "#ffffff";

const SecureCardInfoContentComponent = React.memo(function SecureCardInfoContent(
    props: SecureCardInfoContentProps & BottomSheetChildrenProps,
): React.ReactElement {
    const { data, primaryAction, secondaryAction, close, setAction, onLoad } = props;

    const headerDivRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedElement = useRef<HTMLElement | null>(null);

    useEffect(() => {
        previouslyFocusedElement.current = getActiveElement();
    }, []);

    useEffectOnce(() => {
        typeof onLoad === "function" && onLoad();
    });

    useFocusDialog(headerDivRef, previouslyFocusedElement);

    const handlePrimaryActionClick = useCallback((): void => {
        if (primaryAction) {
            setAction(primaryAction.onClick);
            close();
        }
    }, [close, primaryAction, setAction]);

    const handleSecondaryActionClick = useCallback((): void => {
        if (!secondaryAction) {
            close();
            return;
        }

        // setting after close action to secondary action handler
        setAction(secondaryAction.onClick);
        close();
    }, [secondaryAction, close, setAction]);

    const actionClass = useMemo(
        () =>
            cx(styles.paymentAlertAction, {
                [styles.paymentAlertActionSingle]: !secondaryAction,
            }),
        [secondaryAction],
    );

    const secondaryActionClass = useMemo(() => cx(styles.paymentAlertAction, styles.paymentAlertActionSecondary), []);

    const paymentAlertHeaderClass = useMemo(() => cx(styles.paymentAlertHeader), []);

    const paymentAlertHeaderTitleClass = useMemo(() => cx(styles.paymentAlertHeaderTitle), []);

    const paymentAlertHeaderMessageClass = useMemo(() => cx(styles.paymentAlertHeaderMessage), []);

    const paymentAlertContentClass = useMemo(() => cx(styles.paymentAlertContent), []);

    const paymentAlertContentRowItemClass = useMemo(() => cx(styles.paymentAlertContentRowItem), []);

    const paymentAlertContentRowItemMessageClass = useMemo(() => cx(styles.paymentAlertContentRowItemMessage), []);

    const paymentAlertActionMainClass = useMemo(() => cx(styles.paymentAlertActionMain), []);

    const paymentAlertActionSecondary = useMemo(() => cx(styles.paymentAlertActionSecondary), []);
    return (
        <div
            role={ACCESSIBILITY_ROLES.ALERTDIALOG}
            aria-labelledby={PAYMENT_ALERT_HEADER_TITLE_ID}
            aria-describedby={PAYMENT_ALERT_CONTENT_ID}
        >
            <div className={paymentAlertHeaderClass} data-testid={PAYMENT_ALERT_TEST_IDS.HEADER}>
                <div className={styles.paymentAlertHeaderInfo}>
                    <div className={styles.paymentAlertHeaderContainer}>
                        <div>
                            <div
                                id={PAYMENT_ALERT_HEADER_TITLE_ID}
                                className={paymentAlertHeaderTitleClass}
                                data-testid={PAYMENT_ALERT_TEST_IDS.HEADER_TITLE}
                                ref={headerDivRef as RefObject<HTMLDivElement>}
                                tabIndex={0}
                                role={ACCESSIBILITY_ROLES.HEADING}
                                aria-level={3}
                            >
                                {getCardBrandFormattedString(data.title || "", props.cardBrand)}
                            </div>
                            <div
                                className={paymentAlertHeaderMessageClass}
                                data-testid={PAYMENT_ALERT_TEST_IDS.HEADER_MESSAGE}
                            >
                                {getCardBrandFormattedString(data.message || "", props.cardBrand)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={paymentAlertContentClass} data-testid={PAYMENT_ALERT_TEST_IDS.CONTENT}>
                {data.points && (
                    <div className={styles.paymentAlertContentRow} id={PAYMENT_ALERT_CONTENT_ID}>
                        {data.points.map((point, index) => (
                            <div className={paymentAlertContentRowItemClass} key={index}>
                                <img
                                    crossOrigin="anonymous"
                                    src={getCloudinaryUrl(point.iconUrl, { height: 72 })}
                                    className={styles.paymentAlertContentRowItemIcon}
                                    width={36}
                                    height={36}
                                />
                                <div className={paymentAlertContentRowItemMessageClass}>{point.message}</div>
                            </div>
                        ))}
                    </div>
                )}

                {primaryAction && (
                    <div className={cx(styles.paymentAlertContentRow, styles.paymentAlertContentRowPrimaryAction)}>
                        <SuperButton
                            onClick={handlePrimaryActionClick}
                            buttonType="specialPositive"
                            variant="fullwidth"
                            buttonSize="large"
                            className={actionClass}
                            data-testid={PAYMENT_ALERT_TEST_IDS.PRIMARY_ACTION_BUTTON}
                        >
                            <div className={paymentAlertActionMainClass}>
                                {primaryAction.text}
                                {primaryAction.showArrow && (
                                    <span className={styles.paymentAlertActionArrow}>
                                        <BackIcon color={ARROW_COLOR} />
                                    </span>
                                )}
                            </div>
                        </SuperButton>
                    </div>
                )}

                {secondaryAction && (
                    <div className={cx(styles.paymentAlertContentRow, styles.paymentAlertContentRowSecondaryAction)}>
                        <TextButton
                            onClick={handleSecondaryActionClick}
                            color="special_positive"
                            buttonSize="medium"
                            className={secondaryActionClass}
                            data-testid={PAYMENT_ALERT_TEST_IDS.SECONDARY_ACTION_BUTTON}
                        >
                            <div className={paymentAlertActionSecondary}>{secondaryAction.text}</div>
                        </TextButton>
                    </div>
                )}
            </div>
        </div>
    );
});

function SecureCardInfo(props: CheckboxCardInfoProps): ReactElement {
    const { hideOnOverlayClick = true, onClose, ..._props } = props;
    const [actionOnClose, setActionOnClose] = useState<VoidFunction | undefined>();
    const _onClose = useCallback((): void => {
        actionOnClose && actionOnClose();
        onClose && onClose();
    }, [onClose, actionOnClose]);

    useFocusTrap();
    return (
        <BottomSheet hideOnOverlayClick={hideOnOverlayClick} onClose={_onClose}>
            {(bProps: BottomSheetChildrenProps): React.ReactElement => (
                <SecureCardInfoContentComponent {...bProps} {..._props} setAction={setActionOnClose} />
            )}
        </BottomSheet>
    );
}

export default React.memo(SecureCardInfo);
